import ExcelJS from 'exceljs';
import { ExportDocumentSection } from '../export-task.interfaces';
import { ExportPropertyTypeTransformParams } from '../processors/types/processor-types.interface';
import { ExportFileFormat } from '../export-task.enum';
import { get } from 'lodash';
import { ExportStoreService } from 'app/layout/export-overlay/export-overlay.store';
import { ExportTaskBeforePostprocessing, ExportTaskInternalShared } from '../export-task-internal.interfaces';

interface ExcelPage {
  title: string;
  list: any[][];
  columnCount: number;
}

/** @internal Do not use directly */
export class ExcelGenerator {
  private pages: ExcelPage[] = [];

  constructor(
    private sections: ExportDocumentSection[],
    private store: ExportStoreService,
    private internalShared: ExportTaskInternalShared,
  ) { }

  private getSectionHeaders(section: ExportDocumentSection) {
    const firstItem = section.list[0];
    // With no rows there is no object to read the column order from, so fall back to the
    // localization map. Either way the keys go through the same mapping below: returning them
    // directly would print the raw property names as headers on an empty export.
    const headerKeys = firstItem
      ? Object.keys(firstItem)
      : section.itemLocalization
        ? Object.keys(section.itemLocalization)
        : [];

    return headerKeys.map((key) => {
      if (typeof section.itemLocalization[key] === 'string') {
        return section.itemLocalization[key];
      }
      return key;
    });
  }

  private transformObjectProperties(target: any) {
    Object.entries(target).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        if ((value as any).__for_processor) {
          const tparams = value as ExportPropertyTypeTransformParams;
          if (tparams.formats.includes(ExportFileFormat.Excel)) {
            target[key] = tparams.transform() || tparams.fallback;
          } else {
            target[key] = tparams.fallback;
          }
        }
      }
    });
    return target;
  }

  private generatePages() {
    this.pages = this.sections.map((section) => {
      const page = {
        title: section.title,
        list: [
          // Headers
          this.getSectionHeaders(section),
          // Rows with values - filtramos items nulos/undefined
          ...section.list.filter((item) => item !== null && item !== undefined)
            .map((item) => Object.values(this.transformObjectProperties(item))),
        ],
        columnCount: 0,
      };
      page.list.forEach((group) => {
        if (group && group.length > page.columnCount) {
          page.columnCount = group.length;
        }
      });
      return page;
    });
  }

  async insertImageAtPostTransformation(
    data: Record<string, any>,
    wb: ExcelJS.Workbook,
    sheet: ExcelJS.Worksheet,
    row: number,
    column: number,
  ) {
    const loadImageBuffer = async () => {
      return new Promise<ArrayBuffer>((resolve) => {
        fetch(data.value).then((response) => {
          response.blob().then((blob) => {
            blob.arrayBuffer().then((buffer) => {
              resolve(buffer);
            })
          });
        });
      });
    };

    const getExtension = () => {
      let ext = (data.value.match(/[^.]+$/) || [])[0] || 'jpeg';
      if (ext === 'jpg') ext = 'jpeg';
      if (['jpeg', 'png', 'gif'].includes(ext)) return ext;
      return 'jpeg';
    };

    sheet.addImage(wb.addImage({
      buffer: await loadImageBuffer(),
      extension: getExtension(), 
    }), {
      tl: { row: row, col: column }, 
      ext: { width: 80, height: 80 },
    });

    sheet.getColumn(column + 1).width = 12;
    sheet.getRow(row + 1).height = 80;
  }

  async applyPostTransformation(
    data: Record<string, any>,
    wb: ExcelJS.Workbook,
    sheet: ExcelJS.Worksheet,
    row: number,
    column: number,
  ) {
    if (this.internalShared.isStopped()) return;
    if (data.__post_transform === 'image') {
      await this.insertImageAtPostTransformation(data, wb, sheet, row, column);
      this.internalShared.setIteration(this.internalShared.getIteration() + 1);
      this.store.emitChange();
    }
  }

  preparePostProcessing(taskBeforePostProcessing: ExportTaskBeforePostprocessing) {
    const postProcessingIterations = this.pages.reduce((output, page) => {
      page.list.forEach((values) => (
        values.forEach((value) => {
          if (value && typeof value === 'object' && value.__post_transform) {
            output += 1;
          }
        })
      ))
      return output;
    }, 0);
    if (postProcessingIterations > 0) {
      this.internalShared.setTitle(`${taskBeforePostProcessing.title} (Finalizando...)`);
      this.internalShared.setIterations(postProcessingIterations);
      this.internalShared.setIteration(0);
      this.store.emitChange();
      return true;
    }
    return false;
  }

  async generateWorkBook() {
    this.generatePages();
    const wb = new ExcelJS.Workbook();

    const taskBeforePostProcessing: ExportTaskBeforePostprocessing = {
      title: this.internalShared.getTitle(),
    };
    const postProcessingIsNext = this.preparePostProcessing(taskBeforePostProcessing);

    await Promise.all(this.pages.map((page) => {
      const sheet = wb.addWorksheet(page.title);
      const maxColumnWidth = new Array(page.columnCount).fill(10);
      return Promise.all(page.list.map((values, row) => {
        return Promise.all(values.map(async (value, column) => {
          const cell = sheet.getCell(row + 1, column + 1);
          if (value && typeof value === 'object') {
            if (value.__post_transform) {
              return await this.applyPostTransformation(value, wb, sheet, row, column);
            }
            if (value.format) {
              cell.value = value.v;
              cell.numFmt = value.format;
            } else if (value.formula) {
              cell.value = value;
            } else {
              cell.value = '__INVALID';
            }
          } else {
            cell.value = value;
          }
          maxColumnWidth[column] = Math.max(
            (typeof value === 'string') ? value.length + 1 : 10,
            maxColumnWidth[column],
          );
          sheet.getColumn(column + 1).width = maxColumnWidth[column];
        }));
      }));
    }));

    if (postProcessingIsNext) {
      this.internalShared.setTitle(taskBeforePostProcessing.title);
      this.store.emitChange();
    }

    return wb;
  }
}
